import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { RepeatableList } from '@/components/onboarding/RepeatableList';
import { CharCounter } from '@/components/shared/CharCounter';
import type { SessionSchedule } from '@/services/kbProfileService';
import { CAPS } from '@/hooks/useKbProfileForm';
import { prefersReducedMotion } from '@/lib/formFocus';

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

/** F160 — per-row validation error, index-aligned with the `value` array and
 * threaded down from `OperationStep`'s `errors.operation?.sessions` (react-
 * hook-form + zod resolver shape — mirrors `CatalogStep`'s `errors.catalog`
 * array of per-item field-error objects). `undefined` = that row is valid. */
export interface SessionFieldErrors {
  days?: { message?: string };
  start?: { message?: string };
  duration_min?: { message?: string };
  note?: { message?: string };
}

/** Fields checked, in priority order, for the F160 force-open/flag behavior —
 * mirrors `CatalogStep`'s `CATALOG_FIELD_ORDER`. */
const SESSION_FIELD_ORDER = ['days', 'start', 'duration_min', 'note'] as const;
type SessionField = (typeof SESSION_FIELD_ORDER)[number];

function emptySession(): SessionSchedule {
  return { days: [], start: '' };
}

/** Stable DOM id per (row, field) — used for both the force-open focus
 * target (F160 FR3) and the new-row scroll target. `days` has no single
 * input, so it targets the (tabIndex=-1, programmatically-focusable)
 * chip-group container instead of an input element. */
function sessionFieldId(idx: number, field: SessionField): string {
  if (field === 'days') return `session-days-group-${idx}`;
  if (field === 'start') return `session-start-${idx}`;
  if (field === 'duration_min') return `session-dur-${idx}`;
  return `session-note-${idx}`;
}

interface DayChipProps {
  day: WeekdayKey;
  selected: boolean;
  onToggle: () => void;
  label: string;
}

function DayChip({ day, selected, onToggle, label }: DayChipProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={label}
      onClick={onToggle}
      className={[
        'min-h-11 min-w-11 px-3 rounded-md border text-sm font-medium transition-colors',
        selected
          ? 'bg-foreground text-background border-foreground'
          : 'bg-background text-foreground border-border hover:bg-muted',
      ].join(' ')}
      data-testid={`day-chip-${day}`}
    >
      {label}
    </button>
  );
}

/** F160 FR2 — abbreviated day label for the collapsed-card summary. Derived
 * by truncating the SAME i18n key the day chips use (`Segunda` → `Seg`) so
 * there is no second abbreviation table to drift out of sync (Notes). */
/** F160 FR2 — abbreviated day label for the collapsed-card summary. Derived
 * by truncating the SAME i18n key the day chips use (`Segunda` → `Seg`) so
 * there is no second abbreviation table to drift out of sync (Notes). */
function abbreviateDay(t: (key: string) => string, day: WeekdayKey): string {
  return t(`onboarding.kb.weekday_${day}`).slice(0, 3);
}

/** F160 FR2 — pure composition of the collapsed-card summary label:
 * "Seg, Qua, Sex · 18:00 · 60 min" (founder-confirmed format; `note` is
 * deliberately elided). `null` means the row has no content yet — the
 * caller renders the "Nova sessão" placeholder in that case.
 *
 * `dayLabel`/`durationLabel` are injected (not called via `useTranslation`
 * directly) so this composition is unit-testable independent of the
 * `react-i18next` test mock, which echoes translation KEYS verbatim rather
 * than resolving real pt-BR/pt-PT text — slicing a mocked "Segunda" isn't
 * possible in tests, but the ORDERING/SEPARATOR logic still needs pinning.
 * See `SessionScheduleEditor.test.tsx`'s `buildSessionSummaryLabel` block.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function buildSessionSummaryLabel(
  item: SessionSchedule,
  dayLabel: (day: WeekdayKey) => string,
  durationLabel: (minutes: number) => string
): string | null {
  const orderedDays = WEEKDAY_KEYS.filter((d) => (item.days ?? []).includes(d));
  const start = item.start?.trim();
  if (orderedDays.length === 0 && !start) return null;

  return [
    orderedDays.length ? orderedDays.map(dayLabel).join(', ') : null,
    start || null,
    item.duration_min ? durationLabel(item.duration_min) : null,
  ]
    .filter((p): p is string => Boolean(p))
    .join(' · ');
}

interface SessionSummaryProps {
  item: SessionSchedule;
  /** F160 FR3 minimum bar — when set, renders a visible error flag (icon +
   * translated message) on the summary card, alongside the force-open
   * effect in the parent editor. */
  error?: SessionFieldErrors;
}

/** F160 FR2 — collapsed-card summary. An empty/new row (no days, no start)
 * shows the "Nova sessão" placeholder instead. */
function SessionSummary({ item, error }: SessionSummaryProps) {
  const { t } = useTranslation();

  const errorField = error && SESSION_FIELD_ORDER.find((f) => Boolean(error[f]));
  const errorMessage = errorField ? error?.[errorField]?.message : undefined;

  const composed = buildSessionSummaryLabel(
    item,
    (day) => abbreviateDay(t, day),
    (minutes) => `${minutes} ${t('onboarding.kb.sessions.minutes_unit')}`
  );
  const label = composed ?? t('onboarding.kb.sessions.new_session_placeholder');
  const hasContent = composed !== null;

  return (
    <span className="flex min-w-0 flex-1 flex-col">
      <span
        className={[
          'min-w-0 truncate text-sm',
          hasContent ? 'font-medium' : 'italic text-muted-foreground',
        ].join(' ')}
      >
        {label}
      </span>
      {errorMessage ? (
        <span className="flex items-center gap-1 text-xs text-destructive">
          <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
          {t(errorMessage)}
        </span>
      ) : null}
    </span>
  );
}

interface SessionRowProps {
  item: SessionSchedule;
  idx: number;
  update: (next: SessionSchedule) => void;
}

function SessionRow({ item, idx, update }: SessionRowProps) {
  const { t } = useTranslation();

  function toggleDay(day: WeekdayKey) {
    const current = item.days ?? [];
    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
    update({ ...item, days: next });
  }

  function setField<K extends keyof SessionSchedule>(key: K, val: SessionSchedule[K]) {
    update({ ...item, [key]: val });
  }

  return (
    <div className="flex flex-col gap-3" id={`session-row-${idx}`}>
      {/* Day toggle chips */}
      <div>
        <p className="text-xs text-muted-foreground mb-1">
          {t('onboarding.kb.sessions.days_label')}
        </p>
        <div
          id={sessionFieldId(idx, 'days')}
          tabIndex={-1}
          className="flex flex-wrap gap-1"
          role="group"
          aria-label={t('onboarding.kb.sessions.days_label')}
        >
          {WEEKDAY_KEYS.map((day) => (
            <DayChip
              key={day}
              day={day}
              selected={(item.days ?? []).includes(day)}
              onToggle={() => toggleDay(day)}
              label={t(`onboarding.kb.weekday_${day}`)}
            />
          ))}
        </div>
      </div>

      {/* Start time */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <label className="text-xs text-muted-foreground" htmlFor={sessionFieldId(idx, 'start')}>
            {t('onboarding.kb.sessions.start_label')}
          </label>
          <Input
            id={sessionFieldId(idx, 'start')}
            className="h-11 mt-1"
            type="text"
            inputMode="numeric"
            placeholder={t('onboarding.kb.sessions.start_placeholder')}
            value={item.start ?? ''}
            onChange={(e) => setField('start', e.target.value)}
            aria-label={t('onboarding.kb.sessions.start_label')}
          />
        </div>

        {/* Duration */}
        <div className="flex-1">
          <label
            className="text-xs text-muted-foreground"
            htmlFor={sessionFieldId(idx, 'duration_min')}
          >
            {t('onboarding.kb.sessions.duration_label')}
          </label>
          {/* Text + inputMode numeric, NOT type="number": a native number
              input silently changes its value on mouse-wheel scroll / arrow
              keys while focused (cause of "a duração mudou sozinha para 57"
              when the focused field scrolled with the page). Mirrors the
              `start` field. Digits are parsed out in onChange; the zod schema
              still enforces the 1–1440 range. */}
          <Input
            id={sessionFieldId(idx, 'duration_min')}
            className="h-11 mt-1"
            type="text"
            inputMode="numeric"
            placeholder={t('onboarding.kb.sessions.duration_placeholder')}
            value={item.duration_min ?? ''}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '');
              setField('duration_min', digits === '' ? undefined : (Number(digits) as number));
            }}
            aria-label={t('onboarding.kb.sessions.duration_label')}
          />
        </div>
      </div>

      {/* Note */}
      <div>
        <label className="text-xs text-muted-foreground" htmlFor={sessionFieldId(idx, 'note')}>
          {t('onboarding.kb.sessions.note_label')}
        </label>
        <Input
          id={sessionFieldId(idx, 'note')}
          className="h-11 mt-1"
          placeholder={t('onboarding.kb.sessions.note_placeholder')}
          value={item.note ?? ''}
          maxLength={CAPS.session_note}
          onChange={(e) => setField('note', e.target.value || undefined)}
          aria-label={t('onboarding.kb.sessions.note_label')}
        />
        <CharCounter value={item.note ?? ''} max={CAPS.session_note} mode="near-limit" />
      </div>
    </div>
  );
}

interface SessionScheduleEditorProps {
  value: SessionSchedule[] | undefined;
  onChange: (next: SessionSchedule[]) => void;
  /** F160 FR3 — per-row validation errors, index-aligned with `value`.
   * Threaded down from `OperationStep`'s `useFormState`; omit for a
   * standalone/uncontrolled-error usage. */
  errors?: Array<SessionFieldErrors | undefined>;
}

export function SessionScheduleEditor({ value, onChange, errors }: SessionScheduleEditorProps) {
  // A committed profile can hydrate `sessions` as `null` (absent JSONB) or, in
  // theory, a non-array blob — `value ?? []` would let `null` through to
  // RepeatableList but not a stray object. Coerce defensively so the list
  // always iterates an array (mirrors the schema's non-array→[] coercion).
  const sessions = Array.isArray(value) ? value : [];

  // F160 — accordion: at most one session's full form is open at a time.
  // Refine decision: state stays LOCAL to this editor (pure view state; the
  // editor already owns the add/remove `onChange` calls, so it can fix up
  // indices at the mutation site — `OperationStep` has no reason to know
  // which card is open). Mirrors `CatalogStep`'s F149 wiring exactly.
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // Deferred DOM actions that must wait for the target row to actually be in
  // the document (mirrors `CatalogStep`'s pending-ref pattern).
  const pendingScrollAddRef = useRef<number | null>(null);
  const pendingFocusRef = useRef<{ idx: number; field: SessionField } | null>(null);

  useEffect(() => {
    if (pendingScrollAddRef.current != null) {
      const idx = pendingScrollAddRef.current;
      const el = document.getElementById(`session-row-${idx}`);
      if (el) {
        el.scrollIntoView({
          block: 'nearest',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
        pendingScrollAddRef.current = null;
      }
    }

    if (pendingFocusRef.current) {
      const { idx, field } = pendingFocusRef.current;
      const el = document.getElementById(sessionFieldId(idx, field));
      if (el) {
        el.scrollIntoView({
          block: 'center',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
        el.focus({ preventScroll: true });
        pendingFocusRef.current = null;
      }
    }
  });

  // F160 FR3 — validation auto-expand (preferred behavior; parity with
  // CatalogStep): an invalid collapsed row must never hide its error.
  // Re-fires whenever `errors`' content changes — keyed on a signature, not
  // reference, since RHF's `formState.errors` identity isn't guaranteed to
  // change between renders even when its content did (same caveat CatalogStep
  // documents for `errors.catalog`).
  const lastErrorsSignatureRef = useRef<string | null>(null);
  // Deliberately no deps array (see comment above); the signature guard
  // above prevents any update loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const signature = errors ? JSON.stringify(errors) : null;
    if (signature === lastErrorsSignatureRef.current) return;
    lastErrorsSignatureRef.current = signature;

    if (!errors) return;
    const idx = errors.findIndex((e) => Boolean(e));
    if (idx === -1) return;

    setExpandedIndex(idx);

    const field = SESSION_FIELD_ORDER.find((f) => Boolean(errors[idx]?.[f]));
    if (field) {
      pendingFocusRef.current = { idx, field };
    }
  });

  function handleSessionsChange(next: SessionSchedule[]) {
    const prev = sessions;
    onChange(next);

    if (next.length > prev.length) {
      // RepeatableList's append always pushes to the end.
      const newIdx = next.length - 1;
      setExpandedIndex(newIdx);
      pendingScrollAddRef.current = newIdx;
      return;
    }

    if (next.length < prev.length) {
      // Find which position was removed by locating the first divergence —
      // `remove(idx)` keeps every other item's reference, only shifting
      // positions past idx down by one.
      let removedIdx = prev.length - 1;
      for (let i = 0; i < next.length; i++) {
        if (prev[i] !== next[i]) {
          removedIdx = i;
          break;
        }
      }
      setExpandedIndex((cur) => {
        if (cur == null) return cur;
        if (cur === removedIdx) return null;
        if (cur > removedIdx) return cur - 1;
        return cur;
      });
    }
  }

  return (
    <div data-testid="session-schedule-editor">
      <RepeatableList<SessionSchedule>
        items={sessions}
        onChange={handleSessionsChange}
        emptyItem={emptySession}
        maxEntries={CAPS.session_items}
        addLabelKey="onboarding.kb.sessions.add"
        expandedIndex={expandedIndex}
        onExpandedChange={setExpandedIndex}
        renderSummary={(item, idx) => <SessionSummary item={item} error={errors?.[idx]} />}
        renderRow={(item, idx, update) => <SessionRow item={item} idx={idx} update={update} />}
      />
    </div>
  );
}
